<template>
    <div class="history-controls" :class="{ 'history-controls--compact': compact }" :style="sizeStyle">
        <button-tooltip bottom :tooltip="undoTooltip">
            <template #activator="{ on, attrs }">
                <button type="button" class="history-btn" :class="{ 'history-btn--off': !undoable }" v-bind="attrs"
                    v-on="on" @click="undo">
                    <v-icon :size="compact ? 18 : 15">mdi-undo</v-icon>
                </button>
            </template>
        </button-tooltip>
        <button-tooltip bottom :tooltip="redoTooltip">
            <template #activator="{ on, attrs }">
                <button type="button" class="history-btn" :class="{ 'history-btn--off': !redoable }" v-bind="attrs"
                    v-on="on" @click="redo">
                    <v-icon :size="compact ? 18 : 15">mdi-redo</v-icon>
                </button>
            </template>
        </button-tooltip>
        <v-menu v-if="!compact" v-model="menu" offset-y left min-width="260" max-width="340"
            content-class="history-menu">
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
                <button-tooltip bottom :tooltip="$t('history.history')" :disabled="menu">
                    <template #activator="{ on, attrs }">
                        <button type="button" class="history-btn history-btn--list" v-bind="{ ...attrs, ...menuAttrs }"
                            v-on="{ ...on, ...menuOn }">
                            <v-icon size="14">mdi-chevron-down</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </template>
            <div class="history-menu-body">
                <div class="history-menu-title">{{ $t('history.menu_title') }}</div>
                <v-list v-if="entries.length" dense class="py-1 transparent">
                    <v-list-item v-for="(entry, i) in entries" :key="`${entry.at}-${i}`" link @click="undoTo(i + 1)">
                        <span class="history-menu-n">{{ i + 1 }}</span>
                        <v-list-item-title class="history-menu-label">{{ entry.label }}</v-list-item-title>
                    </v-list-item>
                </v-list>
                <div v-else class="history-menu-empty">{{ $t('history.empty') }}</div>
            </div>
        </v-menu>
    </div>
</template>

<script>
import ButtonTooltip from '@/components/common/ButtonTooltip'
import {
    canRedo, canUndo, disableHistoryShortcuts, enableHistoryShortcuts, historyEntries, isMacPlatform, lastDone,
    lastUndone, redoHistory, undoHistory, undoHistoryTo,
} from '@/utils/History'

export default {
    name: 'HistoryControls',
    components: { ButtonTooltip },
    props: {
        context: {
            type: String,
            required: true,
        },
        active: {
            type: Boolean,
            default: true,
        },
        height: {
            type: Number,
            default: 32,
        },
    },
    data() {
        return {
            menu: false,
        }
    },
    computed: {
        compact(vm = this) {
            return vm.isMobile
        },
        sizeStyle(vm = this) {
            if (vm.compact) return null
            return { height: `${vm.height}px`, '--history-btn-size': `${vm.height}px` }
        },
        undoable(vm = this) {
            return canUndo(vm.context)
        },
        redoable(vm = this) {
            return canRedo(vm.context)
        },
        entries(vm = this) {
            return historyEntries(vm.context)
        },
        modKey() {
            return isMacPlatform() ? '⌘' : 'Ctrl+'
        },
        undoTooltip(vm = this) {
            const entry = lastDone(vm.context)
            if (!entry) return vm.$t('history.nothing_to_undo')
            const keys = vm.compact ? '' : ` (${vm.modKey}Z)`
            return `${vm.$t('history.undo_label', { label: entry.label })}${keys}`
        },
        redoTooltip(vm = this) {
            const entry = lastUndone(vm.context)
            if (!entry) return vm.$t('history.nothing_to_redo')
            const keys = vm.compact ? '' : ` (${isMacPlatform() ? '⌘⇧Z' : 'Ctrl+Y'})`
            return `${vm.$t('history.redo_label', { label: entry.label })}${keys}`
        },
    },
    mounted() {
        if (this.active) enableHistoryShortcuts(this.context)
    },
    beforeDestroy() {
        disableHistoryShortcuts(this.context)
    },
    methods: {
        undo() {
            if (this.undoable) undoHistory(this.context)
        },
        redo() {
            if (this.redoable) redoHistory(this.context)
        },
        undoTo(steps) {
            this.menu = false
            undoHistoryTo(this.context, steps)
        },
    },
    watch: {
        active(val) {
            if (val) enableHistoryShortcuts(this.context)
            else disableHistoryShortcuts(this.context)
        },
    },
}
</script>

<style scoped>
.history-controls {
    display: flex;
    align-items: center;
    height: 32px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
}

.history-btn {
    width: var(--history-btn-size, 32px);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtSecondary-base);
    transition: color .12s, background .12s, opacity .12s;
}

.history-btn .v-icon {
    color: inherit !important;
}

.history-controls > * {
    display: flex;
    height: 100%;
}

.history-controls > * + * .history-btn {
    border-left: 1px solid var(--v-outline-base);
}

.history-btn--list {
    width: 22px;
}

.history-btn--off {
    opacity: .5;
    cursor: default;
}

.theme--dark .history-btn:not(.history-btn--off):hover {
    color: var(--v-primary-base);
    background: rgba(204, 255, 51, .06);
}

.theme--light .history-btn:not(.history-btn--off):hover {
    color: var(--v-accent-base);
    background: rgba(51, 153, 153, .08);
}

.history-controls--compact {
    height: 40px;
}

.history-controls--compact .history-btn {
    width: 40px;
}

.history-menu-body {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

.history-menu-title {
    padding: 10px 14px 8px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.history-menu-n {
    width: 14px;
    margin-right: 10px;
    flex-shrink: 0;
    text-align: right;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.history-menu-label {
    font-size: 13px;
}

.history-menu-empty {
    padding: 12px 14px;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}
</style>
